/** Files larger than this aren't imported (they'd bloat the shared document). */
export const MAX_IMPORT_BYTES = 1024 * 1024;
export const MAX_IMPORT_FILES = 500;
/** Folders that are never worth importing into a shared editor. */
const IGNORED_DIRS = new Set([
  ".git",
  "node_modules",
  ".venv",
  "__pycache__",
  "dist",
  "build",
  ".next",
  "target",
]);

export interface CandidateFile {
  /** Relative path, "/" separated. */
  path: string;
  size: number;
  read(): Promise<ArrayBuffer>;
}

export interface ImportResult {
  files: { path: string; text: string }[];
  skipped: { path: string; reason: "binary" | "too large" | "ignored folder" | "too many files" }[];
}

const decoder = new TextDecoder("utf-8", { fatal: false });

function isIgnored(path: string): boolean {
  return path.split("/").some((segment) => IGNORED_DIRS.has(segment));
}

/** Reads candidates as text, skipping binaries, oversized files, and build/VCS folders. */
export async function prepareImport(candidates: CandidateFile[]): Promise<ImportResult> {
  const result: ImportResult = { files: [], skipped: [] };
  for (const candidate of candidates) {
    const path = candidate.path.replace(/^\/+/, "");
    if (isIgnored(path)) {
      result.skipped.push({ path, reason: "ignored folder" });
    } else if (candidate.size > MAX_IMPORT_BYTES) {
      result.skipped.push({ path, reason: "too large" });
    } else if (result.files.length >= MAX_IMPORT_FILES) {
      result.skipped.push({ path, reason: "too many files" });
    } else {
      const bytes = new Uint8Array(await candidate.read());
      // A NUL byte in the first 8 KB means binary (the same heuristic git uses).
      if (bytes.subarray(0, 8192).includes(0)) result.skipped.push({ path, reason: "binary" });
      else result.files.push({ path, text: decoder.decode(bytes) });
    }
  }
  return result;
}

/** Files from an <input type=file webkitdirectory> or a plain multi-file input. */
export function candidatesFromFileList(list: FileList | File[]): CandidateFile[] {
  return [...list].map((file) => ({
    path: (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name,
    size: file.size,
    read: () => file.arrayBuffer(),
  }));
}

/** Files from a drop, walking into dropped folders. */
export async function candidatesFromDrop(transfer: DataTransfer): Promise<CandidateFile[]> {
  const entries = [...transfer.items]
    .map((item) => (item.kind === "file" ? item.webkitGetAsEntry?.() : null))
    .filter((entry): entry is FileSystemEntry => Boolean(entry));
  if (entries.length === 0) return candidatesFromFileList(transfer.files);
  const out: CandidateFile[] = [];
  const walk = async (entry: FileSystemEntry, prefix: string): Promise<void> => {
    if (entry.isFile) {
      const file = await new Promise<File>((resolve, reject) =>
        (entry as FileSystemFileEntry).file(resolve, reject),
      );
      out.push({ path: `${prefix}${entry.name}`, size: file.size, read: () => file.arrayBuffer() });
    } else if (entry.isDirectory && !IGNORED_DIRS.has(entry.name)) {
      const reader = (entry as FileSystemDirectoryEntry).createReader();
      // readEntries returns batches until empty.
      for (;;) {
        const batch = await new Promise<FileSystemEntry[]>((resolve, reject) =>
          reader.readEntries(resolve, reject),
        );
        if (batch.length === 0) break;
        for (const child of batch) await walk(child, `${prefix}${entry.name}/`);
      }
    }
  };
  for (const entry of entries) await walk(entry, "");
  return out;
}

/** The whole workspace as a .zip (paths preserved). */
export async function zipWorkspace(files: { path: string; text: string }[]): Promise<Uint8Array> {
  // Loaded on first use: only "Download all" needs it.
  const { strToU8, zipSync } = await import("fflate");
  const tree: Record<string, Uint8Array> = {};
  for (const file of files) tree[file.path.replace(/^\/+/, "")] = strToU8(file.text);
  return zipSync(tree, { level: 6 });
}

export function describeSkipped(skipped: ImportResult["skipped"]): string | undefined {
  if (skipped.length === 0) return undefined;
  const counts = new Map<string, number>();
  for (const s of skipped) counts.set(s.reason, (counts.get(s.reason) ?? 0) + 1);
  return `Skipped ${skipped.length} file${skipped.length === 1 ? "" : "s"} (${[...counts]
    .map(([reason, n]) => `${n} ${reason}`)
    .join(", ")}).`;
}
